import axiosClient from "../axiosClient";

// ── Multi-context types ──────────────────────────────────────────────────────

export interface LoginContext {
  context_type: "PLATFORM" | "TENANT";
  scope_id: string | null;
  label: string;
  tenant_name: string | null;
}

export interface PendingContextSelection {
  selection_token: string;
  expires_in_seconds: number;
  contexts: LoginContext[];
}

export interface SwitchContextPayload {
  target_context_type: "PLATFORM" | "TENANT";
  target_scope_id: string | null;
  terminate_current_session: boolean;
}

export interface AgencyLink {
  membership_id: string;
  tenant_id: string;
  tenant_name: string;
  scope_type: "TENANT";
  valid_until: string | null;
  assigned_at: string;
}

// ── Auth API response types ──────────────────────────────────────────────────

// Types for auth API responses (Real Backend)
interface RealLoginResponse {
  success: boolean;
  message: string;
  statusCode: number;
  responseData:
    | {
        action?: undefined;
        email: string;
        user_id: string;
        first_name: string;
        last_name: string;
        user_type: "platform" | "tenant" | "org";
        is_super_admin: boolean;
        roles: string[];
        permissions: string[];
        scope: "platform" | "tenant" | "org";
        scope_id: string;
        parent_agency_name?: string;
        parent_org_name?: string;
        must_change_password: boolean;
      }
    | {
        action: "select_context";
        selection_token: string;
        expires_in_seconds: number;
        contexts: LoginContext[];
      };
  traceMessage: string;
}

interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    scope: "platform" | "tenant" | "org";
    scopeId: string;
    agencyId?: string;
    organizationId?: string;
    parent_agency_name?: string;
    parent_org_name?: string;
    status: string;
  };
  permissions: string[];
}

// Mock data for development/testing
const MOCK_USERS = {
  "super@admin.com": {
    user: {
      id: "usr_super_001",
      email: "super@admin.com",
      name: "田中太郎",
      role: "super_admin",
      scope: "platform" as const,
      scopeId: "platform-root",
      agencyId: "ag_fivevai_hq",
      organizationId: "org_internal",
      parent_agency_name: "Kangol2",
      parent_org_name: "customer 1",
      status: "active",
    },
    permissions: [
      "users.create",
      "users.read",
      "users.update",
      "users.delete",
      "analytics.view",
      "agencies.create",
      "agencies.read",
      "agencies.update",
      "agencies.delete",
      "agencies.manage",

      "roles.create",
      "roles.read",
      "roles.update",
      "roles.delete",
      "roles.manage",
      "permissions.read",
      "permissions.manage",
      "iam.access",
      "iam.roles",
      "iam.permissions",
      "iam.users",
      "csv.create",
      "reports.create",
      "reports.read",
      "reports.update",
      "reports.delete",
      "dashboards.create",
      "dashboards.read",
      "dashboards.update",
    ],
  },
  "agency@admin.com": {
    user: {
      id: "usr_agency_001",
      email: "agency@admin.com",
      name: "佐藤花子",
      role: "agency_admin",
      scope: "tenant" as const,
      scopeId: "ag_digital_solutions",
      agencyId: "ag_digital_solutions",
      status: "active",
    },
    permissions: [
      "users.create",
      "users.read",
      "users.update",
      "users.delete",
      "organizations.create",
      "organizations.read",
      "organizations.update",
      "organizations.delete",
      "iam.access",
      "iam.users",
      "reports.create",
      "reports.read",
      "reports.update",
      "dashboards.create",
      "dashboards.read",
      "dashboards.update",
    ],
  },
  "org@admin.com": {
    user: {
      id: "usr_org_001",
      email: "org@admin.com",
      name: "鈴木次郎",
      role: "org_admin",
      scope: "org" as const,
      scopeId: "org_acme_corp",
      agencyId: "ag_digital_solutions",
      organizationId: "org_acme_corp",
      status: "active",
    },
    permissions: [
      "users.read",
      "users.update",

      "reports.create",
      "reports.read",
      "reports.update",
      "dashboards.create",
      "dashboards.read",
      "dashboards.update",
    ],
  },
  "agency@user.com": {
    user: {
      id: "usr_analyst_001",
      email: "analyst@test.com",
      name: "高橋美咲",
      role: "agency_user",
      scope: "tenant" as const,
      scopeId: "ag_digital_solutions",
      agencyId: "ag_digital_solutions",
      organizationId: "org_acme_corp",
      status: "active",
    },
    permissions: [
      "reports.create",
      "reports.read",
      "reports.update",
      "dashboards.read",
    ],
  },
  "org@user.com": {
    user: {
      id: "usr_viewer_001",
      email: "viewer@test.com",
      name: "山田一郎",
      role: "viewer",
      scope: "org" as const,
      scopeId: "org_acme_corp",
      agencyId: "ag_digital_solutions",
      organizationId: "org_acme_corp",
      status: "active",
    },
    permissions: ["reports.read", "dashboards.read"],
  },
};

const USE_MOCK = import.meta.env.VITE_USE_MOCK_AUTH === "true";

// Log mock mode status in development
if (import.meta.env.DEV && USE_MOCK) {
  console.log(
    "%c🎭 Mock Auth Enabled",
    "background: #78c5ca; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;",
  );
  console.log("📧 Test accounts:");
  console.log("  • super@admin.com (Super Admin - Full Access)");
  console.log("  • agency@admin.com (Agency Admin)");
  console.log("  • org@admin.com (Organization Admin)");
  console.log("  • analyst@test.com (Analyst)");
  console.log("  • viewer@test.com (Viewer)");
  console.log("🔑 Password: anything works in mock mode");
  console.log("📚 See MOCK_AUTH_GUIDE.md for details");
}

// ── Discriminated union result for login ────────────────────────────────────

export type AuthenticatedUser = {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  name: string | undefined;
  role: string;
  scope: "platform" | "tenant" | "org";
  scopeId: string;
  agencyId?: string;
  organizationId?: string;
  parent_agency_name?: string;
  parent_org_name?: string;
  status: string;
};

export type LoginServiceResult =
  | {
      type: "authenticated";
      user: AuthenticatedUser;
      permissions: string[];
      mustChangePassword: boolean;
    }
  | { type: "select_context"; data: PendingContextSelection };

// ── Helper to build AuthenticatedUser from a real API response ──────────────
function buildUser(
  responseData: Extract<
    RealLoginResponse["responseData"],
    { action?: undefined }
  >,
): AuthenticatedUser {
  const user: AuthenticatedUser = {
    id: responseData.user_id,
    email: responseData.email,
    first_name: responseData.first_name,
    last_name: responseData.last_name,
    name:
      `${responseData.first_name} ${responseData.last_name}`.trim() ||
      responseData.email.split("@")[0],
    role: responseData.roles[0] ?? "viewer",
    scope: responseData.scope,
    scopeId: responseData.scope_id,
    status: "active",
  };
  if (responseData.user_type === "tenant") {
    user.agencyId = responseData.scope_id;
  }
  if (responseData.user_type === "org") {
    user.organizationId = responseData.scope_id;
  }
  if (responseData.parent_agency_name !== undefined) {
    user.parent_agency_name = responseData.parent_agency_name;
  }
  if (responseData.parent_org_name !== undefined) {
    user.parent_org_name = responseData.parent_org_name;
  }
  return user;
}

export const authService = {
  // Login - returns user and permissions (tokens set as httpOnly cookies by backend)
  login: async (
    email: string,
    password: string,
  ): Promise<LoginServiceResult> => {
    // MOCK IMPLEMENTATION
    if (USE_MOCK) {
      await new Promise((resolve) => setTimeout(resolve, 800)); // Simulate network delay

      const mockUser = MOCK_USERS[email as keyof typeof MOCK_USERS];

      if (!mockUser) {
        throw new Error("Invalid credentials");
      }

      return {
        type: "authenticated",
        user: mockUser.user,
        permissions: mockUser.permissions,
        mustChangePassword: false,
      };
    }

    // REAL API IMPLEMENTATION
    const { data } = await axiosClient.post<RealLoginResponse>(
      "/iam/authenticate/user",
      { email, password },
    );

    if (!data.success) {
      throw new Error(data.message || "Login failed");
    }

    const { responseData } = data;

    // Multi-context: user must pick a workspace
    if (responseData.action === "select_context") {
      return {
        type: "select_context",
        data: {
          selection_token: responseData.selection_token,
          expires_in_seconds: responseData.expires_in_seconds,
          contexts: responseData.contexts,
        },
      };
    }

    // Transform API response to match our internal User type
    return {
      type: "authenticated" as const,
      user: buildUser(responseData),
      permissions: responseData.permissions,
      mustChangePassword: responseData.must_change_password || false,
    };
  },

  // POST /api/v1/iam/authenticate/select-context
  selectContext: async (payload: {
    selection_token: string;
    selected_context_type: "PLATFORM" | "TENANT";
    selected_scope_id: string | null;
  }): Promise<LoginServiceResult & { type: "authenticated" }> => {
    const { data } = await axiosClient.post<RealLoginResponse>(
      "/iam/authenticate/select-context",
      payload,
    );

    if (!data.success) {
      throw new Error(data.message || "Context selection failed");
    }

    const { responseData } = data;

    if (responseData.action === "select_context") {
      throw new Error(
        "Unexpected select_context response from select-context endpoint",
      );
    }

    return {
      type: "authenticated",
      user: buildUser(responseData),
      permissions: responseData.permissions,
      mustChangePassword: responseData.must_change_password || false,
    };
  },

  // POST /api/v1/iam/authenticate/switch-context
  switchContext: async (
    payload: SwitchContextPayload,
  ): Promise<LoginServiceResult & { type: "authenticated" }> => {
    const { data } = await axiosClient.post<RealLoginResponse>(
      "/iam/authenticate/switch-context",
      payload,
    );

    if (!data.success) {
      throw new Error(data.message || "Context switch failed");
    }

    const { responseData } = data;

    if (responseData.action === "select_context") {
      throw new Error(
        "Unexpected select_context response from switch-context endpoint",
      );
    }

    return {
      type: "authenticated",
      user: buildUser(responseData),
      permissions: responseData.permissions,
      mustChangePassword: responseData.must_change_password || false,
    };
  },

  // GET /api/v1/iam/platform-users/{userId}/agency-links
  getAgencyLinks: async (userId: string): Promise<AgencyLink[]> => {
    const { data } = await axiosClient.get<{
      responseData: { links: AgencyLink[]; total: number };
    }>(`/iam/platform-users/${userId}/agency-links`);
    return data.responseData.links;
  },

  // POST /api/v1/iam/platform-users/{userId}/agency-links
  createAgencyLink: async (
    userId: string,
    payload: { tenant_id: string; role_name: string },
  ): Promise<AgencyLink> => {
    const { data } = await axiosClient.post<{ responseData: AgencyLink }>(
      `/iam/platform-users/${userId}/agency-links`,
      payload,
    );
    return data.responseData;
  },

  // DELETE /api/v1/iam/platform-users/{userId}/agency-links/{tenantId}
  removeAgencyLink: async (userId: string, tenantId: string): Promise<void> => {
    await axiosClient.delete(
      `/iam/platform-users/${userId}/agency-links/${tenantId}`,
    );
  },

  // Get current user and permissions (call on app start)
  me: async () => {
    // MOCK IMPLEMENTATION
    if (USE_MOCK) {
      await new Promise((resolve) => setTimeout(resolve, 300));

      // In mock mode, simulate session - return first user (super admin)
      const mockUser = MOCK_USERS["super@admin.com"];
      return mockUser;
    }

    // REAL API IMPLEMENTATION
    const { data } = await axiosClient.get<MeResponse>("/auth/me");
    return data;
  },

  // Logout - backend will clear httpOnly cookies
  logout: async () => {
    // MOCK IMPLEMENTATION
    if (USE_MOCK) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return; // Mock logout - just clear state
    }

    // REAL API IMPLEMENTATION
    // Backend expects POST /iam/authenticate/logout and returns a message
    const { data } = await axiosClient.post<{
      message: string;
      response_data: null | unknown;
      status_code: number;
    }>("/iam/authenticate/logout");

    // Return backend payload so callers can use the message if needed
    return data;
  },

  // Change password (when must_change_password is true)
  changePassword: async (currentPassword: string, newPassword: string) => {
    const { data } = await axiosClient.post<{
      success: boolean;
      message: string;
    }>("/iam/users/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to change password");
    }

    return data;
  },

  // Refresh token - backend reads refresh token from httpOnly cookie
  refreshToken: async () => {
    const { data } = await axiosClient.post<{ expiresIn: number }>(
      "/auth/refresh",
    );
    // Backend sets new access token cookie automatically
    return data;
  },

  // Check if user is authenticated (backend will validate cookie)
  // Note: For initial check, try calling /auth/me - cookie will be sent automatically
  isAuthenticated: async () => {
    try {
      await axiosClient.get("/auth/me");
      return true;
    } catch {
      return false;
    }
  },

  // Forgot password - request reset token
  forgotPassword: async (email: string) => {
    const { data } = await axiosClient.post<{
      statusCode: number;
      message: string;
      success: boolean;
      responseData?: {
        reset_token?: string; // Only in dev environment
      };
    }>("/iam/authenticate/forgot-password", {
      email,
    });

    return data;
  },

  // Validate reset token before showing password form
  validateResetToken: async (token: string) => {
    const { data } = await axiosClient.post<{
      statusCode: number;
      message: string;
      success: boolean;
      responseData: null;
    }>("/iam/authenticate/validate-reset-token", { token });

    if (!data.success) {
      throw new Error(data.message || "Invalid or expired reset token");
    }

    return data;
  },

  // Reset password with token
  resetPassword: async (token: string, newPassword: string) => {
    const { data } = await axiosClient.post<{
      statusCode: number;
      message: string;
      success: boolean;
    }>("/iam/authenticate/reset-password", {
      token,
      new_password: newPassword,
    });

    if (!data.success) {
      throw new Error(data.message || "Failed to reset password");
    }

    return data;
  },
};
